import React, { memo, useEffect, useMemo, useState } from "react";

// components
import {
    captureError,
    FESTIVE_END_DATE,
    FESTIVE_LOTTIE_ASSETS,
    FESTIVE_LOTTIE_IMAGES_ASSET_COUNT,
    FESTIVE_LOTTIE_IMAGES_ASSET_PATH,
    FESTIVE_START_DATE,
    isDateWithinInterval,
    isEmpty,
    LOTTIE_ASSETS,
    LottieAnimation,
} from "@swiggy-private/core-ui";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// constants
import { POST_ADD_DE_TIP_IMG, FESTIVE_POST_ADD_DE_TIP_IMG } from "../constants";
import { DE_TIP_WIDGET_DATA_TEST_IDS as DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";

// styles
import styles from "../styles/CartDeTip.scss";

// helpers
import { format, getCloudinaryRawUrl, getCloudinaryUrl, TEAM_LINE } from "@swiggy-private/js-utils";

const imageKitInfo = {
    teamLine: TEAM_LINE.PORTAL,
};

const defaultDetip = {
    loop: true,
    autoplay: true,
    backwardSpeed: 1,
    forwardSpeed: 1,
    playIfVisible: true,
    visible: true,
    playLottie: true,
    fileName: getCloudinaryRawUrl(LOTTIE_ASSETS.DE_TIP_MAIN_PRE_TIP_FILE_ID, {}, imageKitInfo),
};

const getSubtitleHeaderPreTipLottieProps = async (isTipAmountPresent: boolean): Promise<any> => {
    const isFestivePeriod = isDateWithinInterval(new Date(), FESTIVE_START_DATE, FESTIVE_END_DATE);

    if (isTipAmountPresent) {
        return null;
    }

    if (isFestivePeriod) {
        try {
            const lottieRequest = await fetch(
                getCloudinaryRawUrl(FESTIVE_LOTTIE_ASSETS.FESTIVE_DE_TIP_MAIN_PRE_TIP_FILE_ID) || "",
            );
            const lottieData = await lottieRequest.json();

            for (let i = 0; i <= FESTIVE_LOTTIE_IMAGES_ASSET_COUNT; i++) {
                const url = `${getCloudinaryUrl(format(FESTIVE_LOTTIE_IMAGES_ASSET_PATH, i))}`;
                const response = await fetch(url);
                const blob = await response.blob();
                const urlBlob = URL?.createObjectURL?.(blob);
                lottieData.assets[i].p = urlBlob;
                lottieData.assets[i].u = "";
            }

            return {
                loop: true,
                autoplay: true,
                backwardSpeed: 1,
                forwardSpeed: 1,
                playIfVisible: true,
                visible: true,
                playLottie: true,
                animationData: lottieData,
            };
        } catch (e) {
            captureError(e);
            // if anything above will get failed we will return the default detip
            return defaultDetip;
        }
    }

    return defaultDetip;
};

const getSubtitleDeTipImageComponentProps = (isTipAmountPresent: boolean): any => {
    const isFestivePeriod = isDateWithinInterval(new Date(), FESTIVE_START_DATE, FESTIVE_END_DATE);

    if (!isTipAmountPresent) {
        return null;
    }
    return {
        width: "100%",
        height: "100%",
        cloudinaryId: isFestivePeriod ? FESTIVE_POST_ADD_DE_TIP_IMG : POST_ADD_DE_TIP_IMG,
        lazyLoad: false,
        customBackground: {
            light: "",
            dark: "",
        },
    };
};

interface DeTipSubtitleLottieProps {
    isTipAmountPresent: boolean;
}

const DeTipSubtitleLottie = ({ isTipAmountPresent }: DeTipSubtitleLottieProps) => {
    const [subtitleHeaderPreTipLottieProps, setSubtitleHeaderPreTipLottieProps] = useState<any>(null);
    const { subtitleHeaderPostTipImgProps } = useMemo(() => {
        return {
            subtitleHeaderPostTipImgProps: getSubtitleDeTipImageComponentProps(isTipAmountPresent),
        };
    }, [isTipAmountPresent]);

    useEffect(() => {
        const fetchLottieProps = async () => {
            const props = await getSubtitleHeaderPreTipLottieProps(isTipAmountPresent);
            setSubtitleHeaderPreTipLottieProps(props);
        };
        fetchLottieProps();
    }, [isTipAmountPresent]);

    const dataTestId = isTipAmountPresent ? DATA_TEST_IDS.IMG_POST_ADD : DATA_TEST_IDS.LOTTIE_PRE_ADD;

    return (
        <div data-testid={dataTestId} aria-hidden="true" className={styles.subtitleLottieContainer}>
            {!isEmpty(subtitleHeaderPreTipLottieProps) && <LottieAnimation {...subtitleHeaderPreTipLottieProps} />}
            {!isEmpty(subtitleHeaderPostTipImgProps) && <Image {...subtitleHeaderPostTipImgProps} fit="contain" />}
        </div>
    );
};

export default memo(DeTipSubtitleLottie);
